import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, DollarSign, Clock, Users, Zap, CheckCircle, Shield } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const API = getApiBase();

const inputClass = 'w-full px-4 py-3 bg-white border border-gray-200 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]/20 transition-colors';

const SBAFinancingPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    businessName: '', revenue: '', fundingAmount: '', notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'sba',
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          phone: form.phone,
          company: form.businessName,
          message: form.notes,
          extra: { revenue: form.revenue, fundingAmount: form.fundingAmount },
        }),
      });
      if (!res.ok) throw new Error(`Submit failed (${res.status})`);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sba-financing-page">
      <header className="bg-black py-4 px-4 md:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"><ArrowLeft className="w-4 h-4" /><span className="tracking-wide">Back to Home</span></a>
          <img src="/logos/sag-logo.png" alt="SAG" className="h-8 w-auto" />
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Business Capital</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4">SBA Financing</h1>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto mb-4">
            An exciting opportunity for small businesses. Easy 15-second application to get options in minutes and funding in hours.
          </p>
          <p className="text-[#1e3a5f] text-sm font-medium mb-8">Only applicable for U.S. based businesses</p>
          <a href="#apply" className="inline-flex items-center gap-2 px-10 py-4 bg-[#C9A227] text-black text-sm tracking-wide uppercase font-medium hover:bg-[#1e3a5f] hover:text-white transition-colors" data-testid="sba-apply-btn">
            Apply Now <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: 'Simple Application', desc: 'Our simple 15-second online application can get you matched with offers in minutes.' },
              { icon: Users, title: 'No Cost Advisor', desc: "You'll get matched with your own dedicated Business Financing Advisor at no cost." },
              { icon: DollarSign, title: 'Larger Amounts', desc: 'Get matched with the best financing options with the highest funding amount.' },
              { icon: Zap, title: 'Express Funding', desc: 'Our Fintech Speed can reduce your SBA loan process to as little as 45 days.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-white border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#C9A227]" />
                  </div>
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Qualification */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Requirements</span>
            <h2 className="text-2xl sm:text-3xl font-light text-black mb-4">What Do You Need To Qualify?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { title: 'No Time in Business Required', desc: 'You can qualify for our top financing options as a startup.' },
              { title: 'No Minimum Monthly Sales', desc: 'No minimum revenue needed to qualify for startup financing options.' },
              { title: '650+ FICO Score', desc: 'There is a minimum 650+ FICO Score required to apply for startup financing.' },
            ].map((item, i) => (
              <div key={i} className="p-6 border border-gray-200">
                <CheckCircle className="w-6 h-6 text-[#C9A227] mb-4" />
                <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="#apply" className="inline-flex items-center gap-2 px-10 py-4 bg-[#1e3a5f] text-white text-sm tracking-wide uppercase font-medium hover:bg-black transition-colors">
              Get Funding Now <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Loan Types */}
      <section className="py-16 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Options</span>
            <h2 className="text-2xl sm:text-3xl font-light text-black mb-4">Types of Small Business Loans</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Small Business Line of Credit',
              'Invoice Financing',
              'Working Capital Loans',
              'Equipment Financing',
              'Term Loans',
              'SBA Small Business Loans',
            ].map((type, i) => (
              <div key={i} className="p-5 bg-white border border-gray-200 flex items-center gap-3">
                <span className="w-2 h-2 bg-[#C9A227] rounded-full flex-shrink-0" />
                <span className="text-gray-700 text-sm">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 md:py-24 px-4 bg-white border-t border-gray-200" id="apply">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Get Started</span>
            <h2 className="text-2xl sm:text-3xl font-light text-black mb-4">Simple 15-Second Application</h2>
            <p className="text-gray-500 text-sm">After completing this application, we'll pair you with a dedicated Business Financing Advisor.</p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="sba-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">First Name *</label>
                  <input type="text" required value={form.firstName} onChange={e => setForm(p => ({...p, firstName: e.target.value}))} className={inputClass} data-testid="sba-first-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Last Name *</label>
                  <input type="text" required value={form.lastName} onChange={e => setForm(p => ({...p, lastName: e.target.value}))} className={inputClass} data-testid="sba-last-name" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email *</label>
                <input type="email" required value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="name@company.com" className={inputClass} data-testid="sba-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Phone *</label>
                <input type="tel" required value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className={inputClass} data-testid="sba-phone" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Business Name *</label>
                <input type="text" required value={form.businessName} onChange={e => setForm(p => ({...p, businessName: e.target.value}))} className={inputClass} data-testid="sba-business" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Estimated Revenue</label>
                  <select value={form.revenue} onChange={e => setForm(p => ({...p, revenue: e.target.value}))} className={inputClass} data-testid="sba-revenue">
                    <option value="">Select</option>
                    <option value="startup">Startup (Pre-Revenue)</option>
                    <option value="0-50k">$0 - $50,000</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-500k">$100,000 - $500,000</option>
                    <option value="500k+">$500,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Funding Amount Needed</label>
                  <select value={form.fundingAmount} onChange={e => setForm(p => ({...p, fundingAmount: e.target.value}))} className={inputClass} data-testid="sba-funding">
                    <option value="">Select</option>
                    <option value="10k-25k">$10,000 - $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-500k">$100,000 - $500,000</option>
                    <option value="500k+">$500,000+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Additional Notes</label>
                <textarea rows={3} value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Tell us about your business and financing needs..." className={`${inputClass} resize-none`} data-testid="sba-notes" />
              </div>
              <button type="submit" disabled={submitting} className="w-full py-4 bg-[#C9A227] text-black text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#1e3a5f] hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-testid="sba-submit">
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>
              {error && <p className="text-center text-red-600 text-xs" data-testid="sba-error">{error}</p>}
              <p className="text-center text-gray-400 text-xs">Only applicable for U.S. based businesses</p>
            </form>
          ) : (
            <div className="text-center py-10" data-testid="sba-success">
              <div className="w-16 h-16 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#C9A227]" />
              </div>
              <h3 className="text-2xl font-light text-black mb-3">Application Received</h3>
              <p className="text-gray-500 text-sm mb-6">A Business Financing Advisor will contact you within 24 hours to discuss your options.</p>
              <p className="text-xs text-gray-400">For immediate assistance: <a href="mailto:info@sebasalliance.com" className="text-[#C9A227]">info@sebasalliance.com</a></p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-light text-black mb-4">Have Questions?</h2>
            <p className="text-gray-500 text-sm">Our Business Financing Advisors will help you find the best financing options for your business.</p>
          </div>
          <div className="text-center">
            <a href="#apply" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A227] text-black text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#1e3a5f] hover:text-white transition-colors">
              Speak With An Advisor <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-black py-6 px-4 text-center">
        <p className="text-white/40 text-xs tracking-wider">Sebastian Alliance Group &bull; SBA Financing</p>
      </footer>
    </div>
  );
};

export default SBAFinancingPage;
